import { useEventCallback } from '@ixigo/iui/utils';
import { useEffect, useRef, useState } from 'react';
import { getPlaceDetails } from '../autocomplete/api';
import {
	getUserLocation,
	isCoords,
	isFormattedAddress,
	parseAddressComponents,
} from '../location-services';
import type { Coords, MapLocation } from '../types';
import { bootstrapGoogleMaps } from './googleMaps';

export interface UseGoogleMapParams {
	defaultValue?: MapLocation;
	zoom?: number;
	apiKey?: string;
	/**
	 * Controlled center. When this changes, map recenters without firing onLocationChange.
	 */
	value?: MapLocation;
	onLocationChange: (location: MapLocation) => void;
	onLoad?: () => void;

	/**
	 * Called when the Google Maps API fails to load.
	 */
	onError?: (error: Error) => void;
}

function isEqual(a: Coords, b: Coords) {
	return a.lat === b.lat && a.lng === b.lng;
}

export function useGoogleMap({
	defaultValue,
	zoom = 15,
	apiKey,
	value,
	onLocationChange,
	onLoad,
	onError,
}: UseGoogleMapParams) {
	const [isMapLoaded, setIsMapLoaded] = useState(false);
	const mapInstanceRef = useRef<google.maps.Map | null>(null);
	const valueRef = useRef<MapLocation>(value);

	if (value !== valueRef.current) {
		valueRef.current = value;
	}

	const reverseGeocode = useEventCallback(
		async (location: MapLocation | Coords) => {
			const geocodingLibrary = await (window.google.maps.importLibrary(
				'geocoding',
			) as Promise<google.maps.GeocodingLibrary>);
			const geocoder = new geocodingLibrary.Geocoder();
			if (!isFormattedAddress(location)) {
				const geocodeResult = await geocoder.geocode({ location });
				const result = geocodeResult.results[0];
				if (result) {
					const locationDetails = {
						...location,
						placeId: result.place_id,
						...parseAddressComponents(result.formatted_address),
					};
					onLocationChange(locationDetails);
				}
			}
		},
	);

	const mapRef: React.RefCallback<HTMLElement> = useEventCallback((element) => {
		let isMounted = true;
		let unmountCallback = () => {};
		const initMap = async () => {
			if (!element || isMapLoaded) return;
			try {
				const currentLocationPromise = defaultValue
					? isCoords(defaultValue)
						? Promise.resolve(defaultValue)
						: getPlaceDetails(defaultValue.placeId)
								.catch(() => null)
								.then((place) => place || getUserLocation())
					: getUserLocation();
				await bootstrapGoogleMaps({ key: apiKey });
				const { Map: Gmap } = await (window.google.maps.importLibrary(
					'maps',
				) as Promise<google.maps.MapsLibrary>);

				if (!isMounted) return;

				const currentLocation = await currentLocationPromise.catch(() => null);

				// without center & zoom, the map tiles are not be visible
				// so we always need to make sure we have a default value for this.
				const mapInstance = new Gmap(element, {
					center: currentLocation,
					zoom,
					disableDefaultUI: true,
					gestureHandling: 'greedy',
					clickableIcons: false,
					disableDoubleClickZoom: true,
					styles: [],
				});

				if (
					defaultValue !== currentLocation &&
					isFormattedAddress(currentLocation)
				) {
					// the provided default location has been resolved with lat, lng and address optionally by calling place details
					onLocationChange(currentLocation);
				} else if (currentLocation) {
					reverseGeocode(currentLocation);
				}

				mapInstanceRef.current = mapInstance;
				let debounceTimer: number;
				// Do reverse geocoding once panning/zooming settles
				const idleListener = mapInstance.addListener(
					'center_changed',
					async () => {
						clearTimeout(debounceTimer);
						debounceTimer = window.setTimeout(async () => {
							const center = mapInstance.getCenter();
							if (center) {
								const newCoords = {
									lat: center.lat(),
									lng: center.lng(),
								} as Coords;
								const value = valueRef.current;
								// If center equals the provided controlled value, skip firing
								if (value && isCoords(value) && isEqual(newCoords, value)) {
									return;
								}
								try {
									reverseGeocode(newCoords);
								} catch (error) {
									console.warn('Failed to get address for map center:', error);
									onLocationChange({
										mainText: '',
										formattedAddress: '',
										lat: newCoords.lat,
										lng: newCoords.lng,
									});
								}
							}
						}, 300);
					},
				);
				setIsMapLoaded(true);
				onLoad?.();
				unmountCallback = () => {
					clearTimeout(debounceTimer);
					idleListener.remove();
				};
			} catch (error: unknown) {
				if (error instanceof Error) {
					onError?.(error);
				} else {
					onError?.(new Error('Failed to load Google Maps'));
				}
			}
		};
		element && initMap();
		if (!element) {
			mapInstanceRef.current = null;
			setIsMapLoaded(false);
		}
		return () => {
			isMounted = false;
			mapInstanceRef.current = null;
			unmountCallback();
		};
	});

	// Recenter map when controlled value changes, handler will detect equality and skip firing
	useEffect(() => {
		if (!value || !mapInstanceRef.current || !isCoords(value)) return;
		mapInstanceRef.current.setCenter({ lat: value.lat, lng: value.lng });
	}, [value]);

	return { mapRef, isMapLoaded, mapInstanceRef };
}
